/*
 * Zeilen (Swimlanes): reine Darstellung. Jede Karte steht in genau einer Zeile;
 * Ziehen in eine andere Zeile ändert die zugehörige Eigenschaft der Karte.
 */
import type { LaneMode } from './filter.js';
import type { CardSummary, Label, Member } from './types.js';

export const NO_LANE = '_';
export const PRIORITY_NAMES = ['Keine Priorität', 'Niedrig', 'Mittel', 'Hoch', 'Dringend'] as const;

export interface Lane {
  key: string;
  title: string;
  /** Farbe/Avatar-Hinweis für die Darstellung */
  kind: 'none' | 'member' | 'priority' | 'label';
  /** Mitglied (owner) bzw. Label (label) */
  refId?: string;
}

/** Zeile einer Karte. Label: das erste Label der Karte in der Reihenfolge des Boards */
export function laneKeyOf(card: Pick<CardSummary, 'ownerId' | 'priority' | 'labelIds'>, mode: LaneMode, labelOrder: string[]): string {
  if (mode === 'owner') return card.ownerId ?? NO_LANE;
  if (mode === 'priority') return String(card.priority ?? 0);
  const first = labelOrder.find((id) => card.labelIds.includes(id));
  return first ?? NO_LANE;
}

/**
 * Zeilen in fester Reihenfolge. Ohne emptyLanes nur Zeilen mit Karten –
 * die „ohne“-Zeile erscheint dann ebenfalls nur bei Bedarf.
 */
export function buildLanes(
  mode: LaneMode,
  opts: { cards: Pick<CardSummary, 'ownerId' | 'priority' | 'labelIds'>[]; members: Pick<Member, 'id' | 'name' | 'role'>[]; labels: Pick<Label, 'id' | 'name' | 'color'>[]; emptyLanes?: boolean },
): Lane[] {
  const labelOrder = opts.labels.map((l) => l.id);
  const used = new Set(opts.cards.map((c) => laneKeyOf(c, mode, labelOrder)));
  let lanes: Lane[];
  if (mode === 'owner') {
    lanes = [...opts.members]
      .filter((m) => used.has(m.id) || (opts.emptyLanes && m.role !== 'viewer' && m.role !== 'commenter'))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .map((m) => ({ key: m.id, title: m.name, kind: 'member' as const, refId: m.id }));
    // Hauptverantwortliche, die nicht mehr Mitglied sind
    for (const k of used) if (k !== NO_LANE && !lanes.some((l) => l.key === k)) lanes.push({ key: k, title: 'Ehemaliges Mitglied', kind: 'member', refId: k });
    lanes.push({ key: NO_LANE, title: 'Ohne Hauptverantwortung', kind: 'none' });
  } else if (mode === 'priority') {
    lanes = [4, 3, 2, 1, 0].map((p) => ({ key: String(p), title: PRIORITY_NAMES[p]!, kind: (p === 0 ? 'none' : 'priority') as Lane['kind'] }));
  } else {
    lanes = opts.labels.map((l) => ({ key: l.id, title: l.name || 'Label ohne Namen', kind: 'label' as const, refId: l.id }));
    lanes.push({ key: NO_LANE, title: 'Ohne Label', kind: 'none' });
  }
  return opts.emptyLanes ? lanes : lanes.filter((l) => used.has(l.key));
}

/** Was sich an der Karte ändert, wenn sie von einer Zeile in eine andere kommt */
export type LaneChange =
  | { type: 'owner'; ownerId: string | null }
  | { type: 'priority'; priority: number }
  | { type: 'labels'; labelIds: string[] };

export function laneChange(card: Pick<CardSummary, 'labelIds'>, mode: LaneMode, from: string, to: string): LaneChange | null {
  if (from === to) return null;
  if (mode === 'owner') return { type: 'owner', ownerId: to === NO_LANE ? null : to };
  if (mode === 'priority') return { type: 'priority', priority: Number(to) };
  // Label: das Zeilen-Label tauschen, andere Labels bleiben
  const ids = card.labelIds.filter((id) => id !== from);
  if (to !== NO_LANE && !ids.includes(to)) ids.unshift(to);
  // „Ohne Label“: alle Labels entfernen, sonst landet die Karte in der Zeile des nächsten Labels
  return { type: 'labels', labelIds: to === NO_LANE ? [] : ids };
}
